import { isEmpty, throttle } from "lodash";
import { CalendarMode, CalendarView } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";

import { useBookingRowsContext } from "../context/booking_rows_context";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";
import { Row } from "./row";

const screenHeightsToRenderAhead = 1;

const filterRows = (rows, offsetTop, dimensions, calendarMode, bookingsByRowKey = {}) => {
  const result = rows.filter(({ key }) => {
    const { top, height } = dimensions[key] || {};
    return (
      top + height + window.innerHeight * screenHeightsToRenderAhead > window.scrollY - offsetTop &&
      top < window.scrollY - offsetTop + (1 + screenHeightsToRenderAhead) * window.innerHeight
    );
  });
  //filter out Other row if no "other" bookings are available
  if (calendarMode === CalendarMode.Projects) {
    return result;
  }
  return result.filter(({ key, type }) => type !== RowType.Other || !isEmpty(bookingsByRowKey[key]));
};

const Rows = ({ startDate, currentDate, endDate, width, offsetTop }) => {
  const {
    calendarMode,
    calendarData: { view, setHeatMapEnabled }
  } = useBookingsOverviewContext();
  const { bookingsByRowKey, rowsByKey, rowsByCalendarMode, rowDimensionsByCalendarMode } =
    useBookingRowsContext();

  useEffect(() => {
    if (view !== CalendarView.Compressed) {
      setHeatMapEnabled && setHeatMapEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const rows = rowsByCalendarMode[calendarMode];
  const rowDimensions = rowDimensionsByCalendarMode[calendarMode];
  const [filteredRows, setFilteredRows] = useState(filterRows(rows, offsetTop, rowDimensions));

  const filterCalendarRows = useCallback(() => {
    if (!rows.length) {
      setFilteredRows([]);
      return;
    }
    if (Object.values(rowDimensions).find(({ top }) => top === undefined)) {
      return;
    }
    const rowsToShow = filterRows(rows, offsetTop, rowDimensions, calendarMode, bookingsByRowKey);
    if (rowsToShow.length) {
      setFilteredRows(rowsToShow);
    }
  }, [rows, rowDimensions, calendarMode, bookingsByRowKey]);

  const throttledFilterCalendarRows = useCallback(throttle(filterCalendarRows, 500), [filterCalendarRows]);

  useEffect(() => {
    window.addEventListener("scroll", throttledFilterCalendarRows);

    return () => {
      window.removeEventListener("scroll", throttledFilterCalendarRows);
    };
  }, [throttledFilterCalendarRows]);

  useEffect(() => {
    filterCalendarRows();
  }, [filterCalendarRows]);

  return (
    <>
      {filteredRows
        .filter(({ key }) => !!rowsByKey[key]) // to not display project rows in workforce tab and the other way around
        .map((row) => (
          <Row
            key={`${(row.parents || []).map(({ key }) => key).join("_")}_${row.key}`}
            row={row}
            width={width}
            startDate={startDate}
            currentDate={currentDate}
            endDate={endDate}
          />
        ))}
    </>
  );
};

Rows.propTypes = {
  width: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  currentDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  offsetTop: PropTypes.number
};

export default Rows;
